import { TokenType, HttpHeader } from "@gain/common-enum";
import {
  interpolatePath,
  buildQueryString,
  sendRequest,
  createProxyClient,
  HttpError,
  DEFAULT_REQUEST_TIMEOUT_MS,
  type RouteEntry,
  type RouteMap,
  type RequestPayload,
  type RetryConfig,
  type RouteContext,
} from "#utils.js";

interface BrowserServiceEntry {
  servicePath: string;
  routeMap: RouteMap;
}

type BrowserRegistry = Record<string, BrowserServiceEntry>;

import type { CommonClientApi } from "#api";

export type BrowserCallOptions = CommonClientApi.BrowserCallOptions;

export interface BrowserClientOptions {
  getToken: (type: TokenType) => string | null;
  refreshToken?: () => Promise<void>;
  onError?: (error: HttpError) => void;
  baseUrl: string;
  revision?: string;
}

let clientOptions: BrowserClientOptions | null = null;

export function initBrowserClient(options: BrowserClientOptions): void {
  clientOptions = options;
}

function getOptions(): BrowserClientOptions {
  if (!clientOptions) {
    throw new Error(
      "Browser API client not initialized. Call initBrowserClient() first.",
    );
  }
  return clientOptions;
}

function buildRetryConfig(): RetryConfig | undefined {
  const { refreshToken } = getOptions();
  if (!refreshToken) return undefined;
  return {
    retries: 1,
    shouldRetry: (error) => error.status === 450,
    executeBeforeRetry: async () => {
      await refreshToken();
    },
  };
}

async function executeRequest(
  servicePath: string,
  route: RouteEntry,
  payload: RequestPayload,
  options: BrowserCallOptions | undefined,
  context: RouteContext,
): Promise<unknown> {
  const { baseUrl, getToken } = getOptions();

  const path = interpolatePath(route.path, payload.params);
  const queryString = buildQueryString(payload.query);
  const url = `${baseUrl}/${servicePath}${path}${queryString}`;

  if (route.requiresCompanyId && !options?.companyId) {
    throw new HttpError(
      `Missing required companyId for route ${context.serviceName}.${context.routeName}`,
      {
        status: 400,
        body: "",
        method: route.method,
        path: `${servicePath}${path}`,
        service: context.serviceName,
        routeName: context.routeName,
      },
    );
  }

  try {
    return await sendRequest({
      url,
      method: route.method,
      headers: () => {
        const headers: Record<string, string> = {
          "x-trace-id": crypto.randomUUID(),
        };
        if (payload.body) {
          headers["content-type"] = "application/json";
        }
        if (clientOptions?.revision) {
          headers["x-revision"] = clientOptions.revision;
        }
        if (options?.companyId) {
          headers[HttpHeader.CompanyId] = options.companyId;
        }
        if (route.token) {
          const token = getToken(route.token);
          if (!token) {
            throw new HttpError(`Missing ${route.token} token`, {
              status: 450,
              body: "",
              method: route.method,
              path: `${servicePath}${path}`,
              service: context.serviceName,
              routeName: context.routeName,
            });
          }
          headers.authorization = `Bearer ${token}`;
        }
        return headers;
      },
      body: payload.body,
      responseType: options?.responseType,
      fetchOptions: options?.fetchOptions,
      timeoutMs: DEFAULT_REQUEST_TIMEOUT_MS,
      formatError: (status, errorBody) =>
        new HttpError(
          `API ${status} from ${route.method} ${servicePath}${path}: ${errorBody}`,
          {
            status,
            body: errorBody,
            method: route.method,
            path: `${servicePath}${path}`,
            service: context.serviceName,
            routeName: context.routeName,
          },
        ),
      retry: buildRetryConfig(),
    });
  } catch (error) {
    if (error instanceof HttpError) {
      getOptions().onError?.(error);
    }
    throw error;
  }
}

export function createBrowserClient(
  registry: BrowserRegistry,
): Record<string, Record<string, (...args: unknown[]) => Promise<unknown>>> {
  return createProxyClient(
    registry,
    (entry, route, payload, options, context) =>
      executeRequest(
        entry.servicePath,
        route,
        payload,
        options as BrowserCallOptions | undefined,
        context,
      ),
  );
}

export function registerBrowserService(
  servicePath: string,
  routeMap: RouteMap,
): BrowserServiceEntry {
  return { servicePath, routeMap };
}
